import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import API from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Wallet, IndianRupee, TrendingUp, TrendingDown, Clock, CheckCircle,
  XCircle, QrCode, Upload, Copy, Share2, Users, Gift, ArrowRight,
  History, CreditCard, Loader2, Eye, Download, RefreshCw, AlertCircle, ShoppingCart, Search, Calendar,
  BookOpen, FileText
} from 'lucide-react';
import WalletRedemptionSection from './WalletRedemptionSection';
import { initiatePayUPayment } from '../lib/payu';

export default function WalletPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [topupRequests, setTopupRequests] = useState([]);
  const [qrConfig, setQrConfig] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [referrals, setReferrals] = useState([]);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Top-up request form
  const [showTopupDialog, setShowTopupDialog] = useState(false);
  const [topupForm, setTopupForm] = useState({ amount: '', utr_number: '', screenshot: null });
  const [submittingTopup, setSubmittingTopup] = useState(false);
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  // Referral Ledger
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Credit Ledger
  const [creditOrders, setCreditOrders] = useState([]);
  const [creditSummary, setCreditSummary] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditSearch, setCreditSearch] = useState('');
  const [creditDateFrom, setCreditDateFrom] = useState('');
  const [creditDateTo, setCreditDateTo] = useState('');
  const [creditVendorType, setCreditVendorType] = useState('all');
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledgerFilter, setLedgerFilter] = useState('');
  const [ledgerVendorType, setLedgerVendorType] = useState('');
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerFilteredTotal, setLedgerFilteredTotal] = useState(null);

  const fetchWalletData = useCallback(async () => {
    try {
      const [balanceRes, txnRes, requestsRes, qrRes, referralRes, myReferralsRes] = await Promise.all([
        API.get('/wallet/balance'),
        API.get('/wallet/transactions?limit=50'),
        API.get('/wallet/topup/my-requests'),
        API.get('/wallet/qr-config'),
        API.get('/referrals/my-code'),
        API.get('/referrals/my-referrals')
      ]);

      setWalletData(balanceRes.data);
      setTransactions(txnRes.data.transactions || []);
      setTopupRequests(requestsRes.data.requests || []);
      if (qrRes.data.has_config) {
        setQrConfig(qrRes.data.config);
      }
      setReferralData(referralRes.data);
      setReferrals(myReferralsRes.data.referrals || []);
    } catch (err) {
      console.error('Failed to fetch wallet data', err);
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWalletData();
  }, [fetchWalletData]);

  // Auto-refresh wallet every 15s
  const walletPollRef = useRef(null);
  useEffect(() => {
    walletPollRef.current = setInterval(() => { if (!document.hidden) fetchWalletData(); }, 15000);
    const onVis = () => { if (!document.hidden) fetchWalletData(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(walletPollRef.current); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchWalletData]);

  // Filter transactions based on search and date range
  useEffect(() => {
    let filtered = [...transactions];
    
    // Hide commission/referral charge transactions (tracked in referral ledger, not wallet)
    filtered = filtered.filter(t => 
      t.type !== 'patient_order_commission' && 
      t.type !== 'walkin_prescription_commission'
    );
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(txn => 
        txn.description?.toLowerCase().includes(query) ||
        txn.type?.toLowerCase().includes(query)
      );
    }
    
    // Date range filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(txn => new Date(txn.created_at) >= fromDate);
    }
    
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(txn => new Date(txn.created_at) <= toDate);
    }
    
    setFilteredTransactions(filtered);
  }, [transactions, searchQuery, dateFrom, dateTo]);

  const clearFilters = () => {
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTopupForm({ ...topupForm, screenshot: file });
      setScreenshotPreview(URL.createObjectURL(file));
    }
  };

  const submitTopupRequest = async () => {
    if (!topupForm.amount || parseFloat(topupForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSubmittingTopup(true);
    try {
      const formData = new FormData();
      formData.append('amount', topupForm.amount);
      if (topupForm.utr_number) {
        formData.append('utr_number', topupForm.utr_number);
      }
      if (topupForm.screenshot) {
        formData.append('screenshot', topupForm.screenshot);
      }

      await API.post('/wallet/topup/request', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Top-up request submitted! Awaiting admin approval.');
      setShowTopupDialog(false);
      setTopupForm({ amount: '', utr_number: '', screenshot: null });
      setScreenshotPreview(null);
      fetchWalletData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingTopup(false);
    }
  };

  const copyReferralCode = () => {
    if (referralData?.code) {
      navigator.clipboard.writeText(referralData.code);
      toast.success('Referral code copied!');
    }
  };

  const shareReferralCode = async () => {
    if (navigator.share && referralData?.code) {
      try {
        await navigator.share({
          title: '1Clin Referral',
          text: `Join 1Clin using my referral code: ${referralData.code}`,
          url: window.location.origin
        });
      } catch (err) {
        // User cancelled share - this is not an error
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      copyReferralCode();
    }
  };

  const fetchReferralLedger = useCallback(async () => {
    setLedgerLoading(true);
    try {
      const params = new URLSearchParams({ page: ledgerPage, limit: 20 });
      if (ledgerSearch) params.append('search', ledgerSearch);
      if (ledgerFilter) params.append('source_type', ledgerFilter);
      if (ledgerVendorType) params.append('vendor_type', ledgerVendorType);
      const res = await API.get(`/wallet/referral-ledger?${params}`);
      setLedgerEntries(res.data.entries || []);
      setLedgerSummary(res.data.summary || null);
      setLedgerTotal(res.data.total || 0);
      setLedgerFilteredTotal(res.data.filtered_total || null);
    } catch (err) {
      console.error('Failed to fetch referral ledger', err);
    }
    setLedgerLoading(false);
  }, [ledgerPage, ledgerSearch, ledgerFilter, ledgerVendorType]);

  const [payingOrderId, setPayingOrderId] = useState(null);
  const [payuLoading, setPayuLoading] = useState(false);

  const handlePayUTopup = async () => {
    if (!topupForm.amount || parseFloat(topupForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setPayuLoading(true);
    try {
      await initiatePayUPayment({
        token: localStorage.getItem('clinic_token'),
        purpose: 'doctor_wallet',
        amount: topupForm.amount
      });
    } catch (err) {
      toast.error(err.message || 'Failed to start PayU payment');
      setPayuLoading(false);
    }
  };

  const handlePayCreditViaPayU = async (order) => {
    setPayingOrderId(order.id);
    try {
      await initiatePayUPayment({
        token: localStorage.getItem('clinic_token'),
        purpose: 'doctor_credit',
        orderId: order.id
      });
    } catch (err) {
      toast.error(err.message || 'Failed to start PayU payment');
      setPayingOrderId(null);
    }
  };

  const handlePayCreditFromWallet = async (order) => {
    setPayingOrderId(order.id);
    try {
      const res = await API.post(`/b2b/orders/my-orders/${order.id}/pay-credit`);
      toast.success(res.data?.message || 'Credit paid from wallet');
      fetchWalletData();
      fetchCreditLedger();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setPayingOrderId(null);
    }
  };

  const fetchCreditLedger = useCallback(async () => {
    setCreditLoading(true);
    try {
      const token = localStorage.getItem('clinic_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/credit/my-credit-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCreditSummary(data);
      }
      const ordRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/b2b/orders/my-orders?payment_method=credit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (ordRes.ok) {
        const ordData = await ordRes.json();
        setCreditOrders(ordData.orders || ordData || []);
      }
    } catch (err) {
      console.error('Failed to fetch credit ledger', err);
    }
    setCreditLoading(false);
  }, []);

  // Client-side filtering for credit orders
  const filteredCreditOrders = creditOrders.filter(order => {
    if (creditSearch) {
      const s = creditSearch.toLowerCase();
      if (!(order.order_number || '').toLowerCase().includes(s) &&
          !(order.vendor_name || '').toLowerCase().includes(s) &&
          !(order.patient_name || '').toLowerCase().includes(s)) return false;
    }
    if (creditVendorType && creditVendorType !== 'all' && order.vendor_type !== creditVendorType) return false;
    if (creditDateFrom) {
      const d = (order.created_at || '').split('T')[0];
      if (d < creditDateFrom) return false;
    }
    if (creditDateTo) {
      const d = (order.created_at || '').split('T')[0];
      if (d > creditDateTo) return false;
    }
    return true;
  });

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'topup': return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'debit': return <TrendingDown className="w-4 h-4 text-red-600" />;
      case 'purchase': return <ShoppingCart className="w-4 h-4 text-red-600" />;
      case 'referral_reward': return <Gift className="w-4 h-4 text-amber-600" />;
      case 'patient_referral_reward': return <Gift className="w-4 h-4 text-amber-600" />;
      case 'patient_order_commission': return <IndianRupee className="w-4 h-4 text-green-600" />;
      case 'subscription': return <CreditCard className="w-4 h-4 text-blue-600" />;
      default: return <History className="w-4 h-4 text-slate-500" />;
    }
  };

  const getRequestStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved': return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected': return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div data-testid="wallet-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            <Wallet className="inline w-8 h-8 mr-2 text-teal-600" />
            My Wallet
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage your clinic wallet balance and transactions</p>
        </div>
        <Button onClick={() => setShowTopupDialog(true)} className="bg-teal-700 hover:bg-teal-800" data-testid="add-funds-btn">
          <IndianRupee className="w-4 h-4 mr-2" /> Add Funds
        </Button>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-teal-600 font-medium">Available Balance</p>
                <p className="text-3xl font-bold text-teal-700 mt-1" data-testid="wallet-balance">
                  ₹{walletData?.balance?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">1 Point = ₹1</p>
              </div>
              <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center">
                <Wallet className="w-7 h-7 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Top-ups</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">
                  ₹{walletData?.total_topups?.toLocaleString() || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Spent</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">
                  ₹{walletData?.total_spent?.toLocaleString() || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="transactions" className="space-y-4">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="w-max min-w-full md:w-auto">
            <TabsTrigger value="transactions" data-testid="tab-transactions" className="text-xs sm:text-sm">
              <History className="w-4 h-4 mr-1" /> Transactions
            </TabsTrigger>
            <TabsTrigger value="topup-requests" data-testid="tab-topup-requests" className="text-xs sm:text-sm whitespace-nowrap">
              <Clock className="w-4 h-4 mr-1" /> Top-up Requests
              {topupRequests.filter(r => r.status === 'pending').length > 0 && (
                <Badge className="ml-1 bg-amber-500 text-[10px]">{topupRequests.filter(r => r.status === 'pending').length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="referrals" data-testid="tab-referrals" className="text-xs sm:text-sm">
              <Users className="w-4 h-4 mr-1" /> Referrals
            </TabsTrigger>
            <TabsTrigger value="redeem" data-testid="tab-redeem" className="text-xs sm:text-sm">
              <CreditCard className="w-4 h-4 mr-1" /> Redeem
            </TabsTrigger>
            <TabsTrigger value="referral-ledger" data-testid="tab-referral-ledger" className="text-xs sm:text-sm whitespace-nowrap" onClick={() => { if (!ledgerSummary) fetchReferralLedger(); }}>
              <BookOpen className="w-4 h-4 mr-1" /> Referral Ledger
            </TabsTrigger>
            <TabsTrigger value="credit-ledger" data-testid="tab-credit-ledger" className="text-xs sm:text-sm whitespace-nowrap" onClick={() => { if (!creditSummary) fetchCreditLedger(); }}>
              <FileText className="w-4 h-4 mr-1" /> Credit Ledger
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Transaction History</CardTitle>
              <CardDescription>Your wallet credits and debits</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
                <div className="md:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search transactions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="pl-9"
                      placeholder="From date"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="pl-9"
                      placeholder="To date"
                    />
                  </div>
                  {(searchQuery || dateFrom || dateTo) && (
                    <Button variant="outline" size="icon" onClick={clearFilters} title="Clear filters">
                      <XCircle className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {filteredTransactions.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <History className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p>{transactions.length === 0 ? 'No transactions yet' : 'No matching transactions'}</p>
                  <p className="text-sm mt-1">{transactions.length === 0 ? 'Add funds to get started' : 'Try adjusting your filters'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 mb-2">Showing {filteredTransactions.length} of {transactions.length} transactions</p>
                  {filteredTransactions.map((txn, idx) => {
                    // Determine if this is a debit (negative amount or debit types)
                    const isDebit = txn.amount < 0 || ['debit', 'subscription', 'purchase', 'order', 'payment'].includes(txn.type);
                    
                    return (
                      <div key={txn.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm ${isDebit ? 'bg-red-50' : 'bg-green-50'}`}>
                            {getTransactionIcon(txn.type)}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{txn.description || txn.type}</p>
                            <p className="text-xs text-slate-500">
                              {(() => {
                                const dateStr = txn.created_at?.endsWith('Z') ? txn.created_at : (txn.created_at + 'Z');
                                const date = new Date(dateStr);
                                return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-IN', {
                                  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
                                });
                              })()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${isDebit ? 'text-red-600' : 'text-green-600'}`}>
                            {isDebit ? '-' : '+'}₹{Math.abs(txn.amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-slate-500">Bal: ₹{txn.balance_after?.toLocaleString() || '-'}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Top-up Requests Tab */}
        <TabsContent value="topup-requests">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Top-up Requests</CardTitle>
              <CardDescription>Track your fund addition requests</CardDescription>
            </CardHeader>
            <CardContent>
              {topupRequests.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p>No top-up requests</p>
                  <Button onClick={() => setShowTopupDialog(true)} className="mt-3 bg-teal-600 hover:bg-teal-700">
                    <IndianRupee className="w-4 h-4 mr-2" /> Add Funds Now
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {topupRequests.map((req, idx) => (
                    <div key={req.id || idx} className="p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-lg font-bold text-teal-700">₹{req.amount?.toLocaleString()}</p>
                          {req.utr_number && (
                            <p className="text-sm text-slate-600 mt-1">UTR: {req.utr_number}</p>
                          )}
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(req.created_at).toLocaleDateString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {getRequestStatusBadge(req.status)}
                          {req.screenshot_url && (
                            <a href={req.screenshot_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                              <Eye className="w-3 h-3" /> View Screenshot
                            </a>
                          )}
                        </div>
                      </div>
                      {req.admin_notes && (
                        <div className="mt-2 p-2 bg-white rounded text-sm text-slate-600">
                          <p className="font-medium text-xs text-slate-500">Admin Note:</p>
                          {req.admin_notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Referrals Tab */}
        <TabsContent value="referrals">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Referral Code Card */}
            <Card className="lg:col-span-1 border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <Gift className="w-5 h-5" /> Your Referral Code
                </CardTitle>
                <CardDescription>Share and earn rewards</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-white rounded-lg border-2 border-dashed border-amber-300 text-center">
                  <p className="text-3xl font-bold text-amber-700 tracking-wider" data-testid="referral-code">
                    {referralData?.code || '---'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={copyReferralCode} variant="outline" className="flex-1">
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </Button>
                  <Button onClick={shareReferralCode} className="flex-1 bg-amber-600 hover:bg-amber-700">
                    <Share2 className="w-4 h-4 mr-2" /> Share
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-800">{referralData?.total_referrals || 0}</p>
                    <p className="text-xs text-slate-500">Total Referrals</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">₹{referralData?.total_rewards_earned || 0}</p>
                    <p className="text-xs text-slate-500">Rewards Earned</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Referrals List */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Your Referrals</CardTitle>
                <CardDescription>Doctors who signed up using your code</CardDescription>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p>No referrals yet</p>
                    <p className="text-sm mt-1">Share your code to start earning rewards</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {referrals.map((ref, idx) => (
                      <div key={ref.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                            <Users className="w-5 h-5 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{ref.referred_name}</p>
                            <p className="text-xs text-slate-500">{ref.referred_phone || ref.referred_clinic}</p>
                            <p className="text-xs text-slate-400">
                              {new Date(ref.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {ref.status === 'credited' ? (
                            <Badge className="bg-green-100 text-green-700">
                              +₹{ref.reward_amount} Credited
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">
                              <Clock className="w-3 h-3 mr-1" /> Pending
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Redeem Tab */}
        <TabsContent value="redeem">
          <WalletRedemptionSection 
            userType="doctor" 
            token={localStorage.getItem('token')} 
            walletBalance={walletData?.balance || 0}
            onRedemptionSuccess={fetchWalletData}
          />
        </TabsContent>

        {/* Referral Ledger Tab */}
        <TabsContent value="referral-ledger">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-teal-600" />
                Software Referral Charges Ledger
              </CardTitle>
              <CardDescription>Record of all referral charges from patient orders and walk-in billing (informational only — no wallet impact)</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Summary Cards */}
              {ledgerSummary && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                    <p className="text-xs text-teal-600 font-medium">Total Referral Charges</p>
                    <p className="text-xl font-bold text-teal-700" data-testid="ledger-grand-total">₹{(ledgerSummary.grand_total || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-600 font-medium">From Patient Orders</p>
                    <p className="text-lg font-bold text-blue-700">₹{(ledgerSummary.patient_orders?.total || 0).toLocaleString()}</p>
                    <p className="text-[10px] text-blue-500">{ledgerSummary.patient_orders?.count || 0} orders</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-600 font-medium">From Walk-in Billing</p>
                    <p className="text-lg font-bold text-amber-700">₹{(ledgerSummary.walkin_billing?.total || 0).toLocaleString()}</p>
                    <p className="text-[10px] text-amber-500">{ledgerSummary.walkin_billing?.count || 0} bills</p>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by patient, vendor name, order..."
                    value={ledgerSearch}
                    onChange={(e) => setLedgerSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchReferralLedger()}
                    className="pl-9"
                    data-testid="ledger-search-input"
                  />
                </div>
                <select
                  value={ledgerFilter}
                  onChange={(e) => { setLedgerFilter(e.target.value); setLedgerPage(1); }}
                  className="border rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="ledger-filter-select"
                >
                  <option value="">All Sources</option>
                  <option value="patient_order">Patient Orders</option>
                  <option value="walkin_billing">Walk-in Billing</option>
                </select>
                <select
                  value={ledgerVendorType}
                  onChange={(e) => { setLedgerVendorType(e.target.value); setLedgerPage(1); }}
                  className="border rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="ledger-vendor-type-filter"
                >
                  <option value="">All Vendor Types</option>
                  <option value="chemist">Chemist</option>
                  <option value="pharma">Pharma</option>
                  <option value="lab">Lab</option>
                  <option value="equipment">Equipment</option>
                </select>
                <Button variant="outline" size="sm" onClick={fetchReferralLedger} data-testid="ledger-refresh-btn">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              {/* Filtered Total */}
              {ledgerFilteredTotal && (ledgerSearch || ledgerFilter || ledgerVendorType) && (
                <div className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded-lg flex items-center justify-between">
                  <p className="text-xs text-violet-600 font-medium">Filtered Results: {ledgerFilteredTotal.count} entries</p>
                  <p className="text-sm font-bold text-violet-700">Total: ₹{(ledgerFilteredTotal.total || 0).toLocaleString()}</p>
                </div>
              )}

              {/* Ledger Entries */}
              {ledgerLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                </div>
              ) : ledgerEntries.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p>No referral charges recorded yet</p>
                  <p className="text-xs mt-1">Charges will appear here when patients order medicines or chemists process walk-in bills linked to your prescriptions</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {ledgerEntries.map((entry, i) => (
                    <div key={entry.id || i} className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-slate-50" data-testid={`ledger-entry-${i}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                          entry.source_type === 'patient_order' ? 'bg-blue-100' : 'bg-amber-100'
                        }`}>
                          {entry.source_type === 'patient_order' 
                            ? <ShoppingCart className="w-4 h-4 text-blue-600" />
                            : <FileText className="w-4 h-4 text-amber-600" />
                          }
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{entry.description}</p>
                          {entry.vendor_names && entry.vendor_names.length > 0 && (
                            <p className="text-xs text-teal-600 font-medium">{entry.vendor_names.join(', ')}</p>
                          )}
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge className={entry.source_type === 'patient_order' ? 'bg-blue-100 text-blue-700 text-[10px]' : 'bg-amber-100 text-amber-700 text-[10px]'}>
                              {entry.source_type === 'patient_order' ? 'Patient Order' : 'Walk-in Billing'}
                            </Badge>
                            {entry.vendor_type && (
                              <Badge className="bg-slate-100 text-slate-600 text-[10px] capitalize">{entry.vendor_type}</Badge>
                            )}
                            <span className="text-[10px] text-slate-400">
                              {entry.created_at ? new Date(entry.created_at).toLocaleString() : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm font-bold text-teal-700">₹{(entry.charge_amount || 0).toLocaleString()}</p>
                    </div>
                  ))}
                  {/* Pagination */}
                  {ledgerTotal > 20 && (
                    <div className="flex justify-center gap-2 pt-3">
                      <Button variant="outline" size="sm" disabled={ledgerPage <= 1} onClick={() => { setLedgerPage(p => p - 1); fetchReferralLedger(); }}>Previous</Button>
                      <span className="text-sm text-slate-500 self-center">Page {ledgerPage}</span>
                      <Button variant="outline" size="sm" disabled={ledgerEntries.length < 20} onClick={() => { setLedgerPage(p => p + 1); fetchReferralLedger(); }}>Next</Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Credit Ledger Tab */}
        <TabsContent value="credit-ledger">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> Credit Orders Ledger</CardTitle>
                  <CardDescription>Track all orders purchased on credit from vendors</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchCreditLedger} data-testid="credit-ledger-refresh">
                  <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="bg-slate-50 p-3 rounded-lg mb-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[180px]">
                    <Label className="text-xs text-slate-500">Search</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input placeholder="Order #, vendor..." value={creditSearch} onChange={(e) => setCreditSearch(e.target.value)} className="pl-10 bg-white h-9" data-testid="credit-search" />
                    </div>
                  </div>
                  <div className="w-[120px]">
                    <Label className="text-xs text-slate-500">Vendor Type</Label>
                    <select className="w-full h-9 rounded-md border px-2 text-sm bg-white" value={creditVendorType} onChange={(e) => setCreditVendorType(e.target.value)} data-testid="credit-vendor-type">
                      <option value="all">All</option>
                      <option value="lab">Lab</option>
                      <option value="pharma">Pharma</option>
                      <option value="inventory">Equipment</option>
                      <option value="marketing">Marketing</option>
                    </select>
                  </div>
                  <div className="w-[130px]">
                    <Label className="text-xs text-slate-500">From</Label>
                    <Input type="date" value={creditDateFrom} onChange={(e) => setCreditDateFrom(e.target.value)} className="bg-white h-9" />
                  </div>
                  <div className="w-[130px]">
                    <Label className="text-xs text-slate-500">To</Label>
                    <Input type="date" value={creditDateTo} onChange={(e) => setCreditDateTo(e.target.value)} className="bg-white h-9" />
                  </div>
                  {(creditSearch || creditDateFrom || creditDateTo || creditVendorType !== 'all') && (
                    <Button variant="ghost" size="sm" className="h-9" onClick={() => { setCreditSearch(''); setCreditDateFrom(''); setCreditDateTo(''); setCreditVendorType('all'); }}>
                      <RefreshCw className="w-3 h-3 mr-1" /> Clear
                    </Button>
                  )}
                </div>
              </div>

              {creditLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : (
                <>
                  {/* Credit Summary */}
                  {creditSummary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-blue-600">Total Credit</p>
                        <p className="text-lg font-bold text-blue-700">₹{creditSummary.total_credit_amount?.toLocaleString() || 0}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-green-600">Paid</p>
                        <p className="text-lg font-bold text-green-700">₹{creditSummary.total_paid?.toLocaleString() || 0}</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-amber-600">Outstanding</p>
                        <p className="text-lg font-bold text-amber-700">₹{creditSummary.outstanding?.toLocaleString() || 0}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3 text-center border">
                        <p className="text-xs text-slate-500">Credit Orders</p>
                        <p className="text-lg font-bold text-slate-700">{creditSummary.total_credit_orders || 0}</p>
                      </div>
                    </div>
                  )}

                  {/* Credit Orders List */}
                  {filteredCreditOrders.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>{creditOrders.length > 0 ? 'No orders match filters' : 'No credit orders yet'}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredCreditOrders.map(order => (
                        <div key={order.id} className="border rounded-lg p-3 flex items-center justify-between">
                          <div>
                            <p className="font-mono text-sm text-slate-500">{order.order_number}</p>
                            <p className="font-medium">{order.vendor_name} <span className="text-xs text-slate-400">({order.vendor_type})</span></p>
                            <p className="text-xs text-slate-400">
                              {new Date(order.created_at).toLocaleDateString()} — Due: {order.credit_due_date ? new Date(order.credit_due_date).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">₹{order.total?.toLocaleString()}</p>
                            <Badge className={
                              order.credit_status === 'paid' ? 'bg-green-100 text-green-700' :
                              order.credit_status === 'overdue' ? 'bg-red-100 text-red-700' :
                              order.credit_status === 'partial' ? 'bg-amber-100 text-amber-700' :
                              'bg-blue-100 text-blue-700'
                            }>{order.credit_status || 'pending'}</Badge>
                            {order.credit_amount_paid > 0 && order.credit_status !== 'paid' && (
                              <p className="text-xs text-slate-400 mt-1">Paid: ₹{order.credit_amount_paid?.toLocaleString()}</p>
                            )}
                            {order.credit_status !== 'paid' && (
                              <div className="space-y-1.5 mt-2">
                                <Button
                                  size="sm"
                                  className="h-8 text-xs bg-teal-700 hover:bg-teal-800 text-white w-full"
                                  onClick={() => handlePayCreditFromWallet(order)}
                                  disabled={payingOrderId === order.id}
                                  data-testid={`pay-from-wallet-${order.id}`}
                                >
                                  {payingOrderId === order.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Wallet className="w-3.5 h-3.5 mr-1" />}
                                  Pay from Wallet
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 text-xs border-teal-600 text-teal-700 hover:bg-teal-50 w-full"
                                  onClick={() => handlePayCreditViaPayU(order)}
                                  disabled={payingOrderId === order.id}
                                  data-testid={`pay-via-payu-${order.id}`}
                                >
                                  <CreditCard className="w-3.5 h-3.5 mr-1" /> Pay via PayU
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <Dialog open={showTopupDialog} onOpenChange={setShowTopupDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-teal-600" /> Add Funds to Wallet
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-2">
            {/* QR Code Section */}
            {qrConfig && (
              <div className="text-center p-4 bg-slate-50 rounded-lg border">
                <p className="text-sm font-medium text-slate-600 mb-3">Scan & Pay via UPI</p>
                {qrConfig.qr_image_url ? (
                  <img src={qrConfig.qr_image_url} alt="Payment QR" className="w-40 h-40 mx-auto object-contain bg-white rounded-lg shadow" />
                ) : (
                  <div className="w-40 h-40 mx-auto bg-white rounded-lg shadow flex items-center justify-center">
                    <QrCode className="w-20 h-20 text-slate-300" />
                  </div>
                )}
                <div className="mt-2 space-y-1">
                  <p className="text-sm font-medium text-slate-800">{qrConfig.payee_name}</p>
                  <p className="text-sm text-teal-600">{qrConfig.upi_id}</p>
                </div>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => {
                  navigator.clipboard.writeText(qrConfig.upi_id);
                  toast.success('UPI ID copied!');
                }}>
                  <Copy className="w-3 h-3 mr-1" /> Copy UPI ID
                </Button>
              </div>
            )}

            {!qrConfig && (
              <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                <p className="text-sm text-amber-700">Payment QR not configured. Please contact admin.</p>
              </div>
            )}

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-600 mb-3">After payment, submit details:</p>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Amount Paid (₹) *</Label>
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={topupForm.amount}
                    onChange={(e) => setTopupForm({ ...topupForm, amount: e.target.value })}
                    className="mt-1"
                    data-testid="topup-amount-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-2 border-teal-600 text-teal-700 hover:bg-teal-50"
                    onClick={handlePayUTopup}
                    disabled={payuLoading}
                    data-testid="payu-instant-recharge-btn"
                  >
                    {payuLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    Pay Instantly via PayU
                  </Button>
                  <p className="text-xs text-slate-400 mt-1 text-center">OR submit manual UTR details below</p>
                </div>
                <div>
                  <Label className="text-sm">UTR / Transaction ID (Optional)</Label>
                  <Input
                    placeholder="Enter UTR number"
                    value={topupForm.utr_number}
                    onChange={(e) => setTopupForm({ ...topupForm, utr_number: e.target.value })}
                    className="mt-1"
                    data-testid="topup-utr-input"
                  />
                </div>
                <div>
                  <Label className="text-sm">Payment Screenshot (Optional)</Label>
                  <div className="mt-1 border-2 border-dashed border-slate-300 rounded-lg p-3 text-center">
                    {screenshotPreview ? (
                      <div className="space-y-2">
                        <img src={screenshotPreview} alt="Screenshot Preview" className="max-h-32 mx-auto rounded-lg" />
                        <Button variant="outline" size="sm" onClick={() => {
                          setScreenshotPreview(null);
                          setTopupForm({ ...topupForm, screenshot: null });
                        }}>
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <Upload className="w-6 h-6 mx-auto text-slate-400 mb-1" />
                        <p className="text-xs text-slate-500">Click to upload screenshot</p>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleScreenshotChange}
                          data-testid="topup-screenshot-input"
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => setShowTopupDialog(false)}>Cancel</Button>
            <Button onClick={submitTopupRequest} disabled={submittingTopup} className="bg-teal-700 hover:bg-teal-800" data-testid="submit-topup-btn">
              {submittingTopup ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
